/**
 * Paquete ZIP completo para entregar al contador.
 *
 * Estructura:
 *   axorial-finanzas-YYYY-MM/
 *     axorial-finanzas-YYYY-MM.xlsx      ← reporte branded
 *     documentos/
 *       YYYY-MM-DD - <título tx 1>/
 *         <CFDI PDF>.pdf
 *         <CFDI XML>.xml
 *       YYYY-MM-DD - <título tx 2>/
 *         …
 *
 * Las tx sin documentos no generan carpeta. Tx con `documentedExternally`
 * van sin carpeta también (el usuario declaró que vive afuera).
 *
 * Descarga vía blob + anchor click. Las descargas de cada documento
 * van en serie para no saturar al Worker; con 100+ docs puede tardar
 * varios segundos — el caller debe mostrar progreso.
 */
import type { Transaction } from "@axorial/shared";
import { exportTransactionsToExcel } from "./exportExcel";

type TxWithMeta = Transaction & {
  companyName: string | null;
  documents?: { id: string; filename: string }[];
};

interface PackageInput {
  transactions: TxWithMeta[];
  period: string | null;
  accountById: Map<string, string>;
  categoryById: Map<string, string>;
  /** Callback opcional para progreso: stage + n procesados / total */
  onProgress?: (info: {
    stage: "excel" | "docs" | "zipping";
    processed: number;
    total: number;
  }) => void;
}

/**
 * Sanitiza un string para que sea válido como nombre de carpeta/archivo
 * en Windows, macOS y Linux. Reemplaza / \ : * ? " < > | y trims.
 */
function safeName(input: string, max = 80): string {
  const cleaned = input
    .replace(/[/\\:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.length > max ? cleaned.slice(0, max).trim() : cleaned || "sin-titulo";
}

function txFolderName(t: TxWithMeta): string {
  const date = t.paymentDate ?? t.date;
  const title =
    t.title?.trim() || t.description?.trim() || t.companyName || t.counterparty || "Transacción";
  return `${date} - ${safeName(title)}`;
}

export async function exportFinancePackageZip(input: PackageInput): Promise<void> {
  const { transactions, period, accountById, categoryById, onProgress } = input;

  const JSZipMod = await import("jszip");
  // El default es la clase JSZip (constructor); la importación a veces
  // viene como módulo entero según el bundler. Tomamos default si está.
  const JSZipCtor = (JSZipMod as { default: typeof import("jszip") }).default;
  const zip = new JSZipCtor();

  // ── 1. Reporte Excel branded ───────────────────────────────────────
  onProgress?.({ stage: "excel", processed: 0, total: 1 });
  const xlsxBuffer = await exportTransactionsToExcel(
    { transactions, period, accountById, categoryById },
    { returnBuffer: true },
  );
  const xlsxName = period
    ? `axorial-finanzas-${period}.xlsx`
    : `axorial-finanzas-${new Date().toISOString().slice(0, 10)}.xlsx`;
  if (xlsxBuffer) zip.file(xlsxName, xlsxBuffer);
  onProgress?.({ stage: "excel", processed: 1, total: 1 });

  // ── 2. Documentos por transacción ──────────────────────────────────
  const txsWithDocs = transactions.filter((t) => (t.documents?.length ?? 0) > 0);
  const totalDocs = txsWithDocs.reduce((s, t) => s + (t.documents?.length ?? 0), 0);
  let processed = 0;
  onProgress?.({ stage: "docs", processed: 0, total: totalDocs });

  const docsFolder = zip.folder("documentos");
  if (!docsFolder) throw new Error("No pude crear la carpeta documentos en el ZIP");

  for (const tx of txsWithDocs) {
    const folder = docsFolder.folder(txFolderName(tx));
    if (!folder) continue;
    for (const doc of tx.documents ?? []) {
      try {
        const res = await fetch(`/api/finance/documents/${doc.id}/download`);
        if (!res.ok) {
          console.warn(`download falló doc=${doc.id} status=${res.status}`);
          processed += 1;
          onProgress?.({ stage: "docs", processed, total: totalDocs });
          continue;
        }
        const blob = await res.blob();
        const ab = await blob.arrayBuffer();
        folder.file(safeName(doc.filename, 120), ab);
      } catch (e) {
        console.error("error descargando doc", doc.id, e);
      }
      processed += 1;
      onProgress?.({ stage: "docs", processed, total: totalDocs });
    }
  }

  // ── 3. Genera el ZIP y dispara descarga ────────────────────────────
  onProgress?.({ stage: "zipping", processed: 0, total: 1 });
  const zipBlob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
  onProgress?.({ stage: "zipping", processed: 1, total: 1 });

  const zipName = period
    ? `axorial-finanzas-${period}.zip`
    : `axorial-finanzas-${new Date().toISOString().slice(0, 10)}.zip`;
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = zipName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
